import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { numberToDate, toHttpParams, FDateV2 } from '@shared/util';
import parseISO from 'date-fns/parseISO';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { setDeadlineDay } from '@layout/layout.store';
import { subDays, addDays, differenceInCalendarDays } from 'date-fns';
import { Bank, Enterprise, setEnterprise } from '../store';

@Injectable({ providedIn: 'root' })
export class EnterpriseService {
  groupListList;

  constructor(private http: HttpClient, private store: Store) {}

  register(
    licenseCode: string,
    groupName: string,
    enterpriseName: string,
    orgCode?: string,
    enterpriseInfo?: string
  ): Observable<{ res: boolean; message: string; data: { needActive: boolean; userLogin: string } }> {
    let params = new HttpParams();
    params = params
      .set('licenseCode', licenseCode)
      .set('groupName', groupName)
      .set('enterpriseName', enterpriseName)
      .set('orgCode', orgCode)
      .set('enterpriseInfo', enterpriseInfo);

    return this.http
      .post('/account/registerenterprise', params)
      .pipe(map((resp: any) => ({ res: !!resp.userLogin, message: resp.message, data: resp })));
  }

  join(): Observable<{ res: boolean; message: string; data: { needActive: boolean; userLogin: string } }> {
    let params = new HttpParams();
    params = params.set('authorizecode', 'cfa');
    return this.http
      .post('/account/registerenterprise/authorize', params)
      .pipe(map((resp: any) => ({ res: !!resp.userLogin, message: resp.message, data: resp })));
  }

  enterprise(): Observable<Enterprise> {
    return this.http.get('/personal/getCurrentEnterprise').pipe(
      map((res: { [key: string]: any }) => {
        // 公司信息
        const formattedRes = this.transformEnterprise(res);
        this.store.dispatch(setEnterprise({ ...formattedRes, ...{ parent: {} } }));
        return formattedRes;
      })
    );
  }

  enterpriseList(): Observable<Enterprise[]> {
    return this.http
      .get<Enterprise[]>('/personal/getEnterpriseList')
      .pipe(map(resArray => resArray.map(res => this.transformEnterprise(res))));
  }

  getNewEnterpriseList(): Observable<Enterprise[]> {
    return this.http
      .get<Enterprise[]>('/datamaintain/enterprise/getNewEnterpriseList')
      .pipe(map((resp: Enterprise[]) => resp));
  }

  currencyList(): Observable<string[]> {
    return this.http.get<string[]>('/personal/getCurrencyList');
  }

  swapEnterpriseCurrency(enterpriseId: number, currency: string): Observable<boolean> {
    return this.http.get(`/personal/company/switch/${enterpriseId}/${currency}`).pipe(
      map(
        (data: { switchToCurrency: string; switchToCurrencyTo: string; switchToEnterpriseId: number }) =>
          //data && data.switchToEnterpriseId === enterpriseId && data.switchToCurrencyTo === currency
          data && data.switchToEnterpriseId === enterpriseId
      )
    );
  }

  private transformEnterprise(resEnterprise: { [key: string]: any }): Enterprise {
    resEnterprise.creationDate = parseISO(resEnterprise.creationDate);
    resEnterprise.valid = resEnterprise.valid !== 2;
    return resEnterprise as Enterprise;
  }

  companyList(): Observable<Enterprise[]> {
    return this.http
      .get<{ companiesInGroup: Enterprise[] }>('/personal/company/getPermittedCompanies')
      .pipe(map(data => (data.companiesInGroup ?? []).map(res => this.transformEnterprise(res))));
  }

  groupList() {
    return this.http
      .get<{ groupListList: Enterprise[] }>(`/personal/group/getCurrentGroup`)
      .pipe(map(data => (data.groupListList ?? []).map(res => this.transformEnterprise(res))));
  }

  commonBankList(bankName?: string) {
    const params: { bankName?: string } = {};
    if (bankName) {
      params.bankName = bankName;
    }

    return this.http.get<Bank[]>(bankName == null ? '/common/listCommonBank' : '/common/findCommonBank', { params });
  }

  queryServiceExpirationInfo() {
    return this.http.get<any>('/personal/company/serviceExpirationInfo').pipe(
      map((res: any) => {
        let deadline, deadlineDay;
        if (res.message.includes('success')) {
          deadline = FDateV2(res.serviceExpirationDateId, 'yyyyMMdd', new Date());
          deadlineDay = differenceInCalendarDays(deadline, new Date());
          if (deadlineDay >= 1) {
            deadlineDay = deadlineDay + 1;
          }
          this.store.dispatch(setDeadlineDay({ deadlineDay }));
        } else {
          //未激活 限制使用 弹出激活框
          if (res.message === 'service.not.actived') {
            deadlineDay = -1;
            this.store.dispatch(setDeadlineDay({ deadlineDay }));
          }
        }

        return { res: res.message, message: deadline };
      })
    );
  }

  activeService(params) {
    return this.http.post<{ message: string }>('/personal/company/activeService', toHttpParams(params));
  }
}
